/**
 * A deploy while the portal is open removes the old build's chunks, so a
 * lazy import (the customer sheet) fails with a 404. This spots that, shows
 * "a new version is available" with a Reload button, reloads once on its own
 * when Vite reports a failed preload, and checks /version.json every 5 minutes.
 */
import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '@/lib/storage';

declare const __BUILD_ID__: string;

const CHUNK_ERRORS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
  'unable to preload css',
  'failed to load module script',
  "unexpected token '<'",
  'is not a valid javascript mime type',
  'loading chunk',
  'loading css chunk',
];

/** True for the errors a browser raises when a lazy chunk cannot load. */
export function isChunkLoadError(error: unknown): boolean {
  let message = '';
  if (error instanceof Error) message = error.message;
  else if (typeof error === 'string') message = error;
  const lower = message.toLowerCase();
  return CHUNK_ERRORS.some((fragment) => lower.includes(fragment));
}

let stale = false;
const listeners = new Set<() => void>();

export function markStaleBuild(): void {
  if (stale) return;
  stale = true;
  for (const listener of listeners) listener();
}

export function useStaleBuild(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => stale,
    () => false,
  );
}

export function reloadPage(): void {
  window.location.reload();
}

const RELOADED_KEY = `sao-stale-reload:${typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : ''}`;
const POLL_MS = 5 * 60_000;

/** Pure: has the deployed build changed from the one running? */
export function buildChanged(running: string, deployed: unknown): boolean {
  const build = (deployed as { build?: unknown } | null)?.build;
  return typeof build === 'string' && build !== running;
}

async function checkVersion(): Promise<void> {
  try {
    const response = await window.fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return;
    if (buildChanged(__BUILD_ID__, await response.json())) markStaleBuild();
  } catch {
    // Offline or no version file: nothing to say.
  }
}

/**
 * Reload once per build (same URL); a second failure shows the prompt instead.
 * Returns true when a reload was started.
 */
function reloadOnceOrMarkStale(): boolean {
  if (readStorage(RELOADED_KEY, 'session') === null) {
    writeStorage(RELOADED_KEY, '1', 'session');
    reloadPage();
    return true;
  }
  markStaleBuild();
  return false;
}

/** A route or page failed: if it was a chunk error, recover; always re-check the version. */
export function handleRouteError(error: unknown): boolean {
  if (import.meta.env.PROD) void checkVersion();
  return isChunkLoadError(error) && reloadOnceOrMarkStale();
}

export function installStaleBuildGuard(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnceOrMarkStale();
  });
  if (import.meta.env.PROD) {
    setInterval(() => void checkVersion(), POLL_MS);
    window.addEventListener('focus', () => void checkVersion());
  }
}
